import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, LineChart, Line, Legend, CartesianGrid } from 'recharts';
import axios from 'axios';
import { InformationCircleIcon } from '@heroicons/react/20/solid';
import TradingViewWidget from './TradingViewWidget';
import TradingViewSymbolProfileWidget from './TradingViewSymbolProfileWidget';
import CompanyDescriptionWidget from './CompanyDescriptionWidget';
import DisclaimerFooter from './DisclaimerFooter';
import { signalDescriptions } from '../constants/signalDescriptions';
import { formatLocalDate } from '../utils/dates';

const componentScoreSeries = [
  { key: 'value_score', label: 'Value', color: '#2E5BFF', defaultVisible: true },
  { key: 'momentum_score', label: 'Momentum', color: '#00D4FF', defaultVisible: true },
  { key: 'quality_score', label: 'Quality', color: '#22C55E', defaultVisible: true },
  { key: 'lowvol_score', label: 'Low Vol', color: '#F59E0B', defaultVisible: true },
  { key: 'earnings_score', label: 'Earnings', color: '#EF4444', defaultVisible: true },
  { key: 'transcripts_score', label: 'Transcripts', color: '#8B5CF6', defaultVisible: true },
  { key: 'innovation_score', label: 'Innovation', color: '#EC4899', defaultVisible: true },
];

const formatMonthYear = (date) => formatLocalDate(date, { month: 'short', year: 'numeric' });

export default function TickerPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const { securityId, cusip } = useParams();
  const { security, returnTo } = location.state || {};
  const [securityData, setSecurityData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTooltip, setActiveTooltip] = useState(null);
  const [visibleComponents, setVisibleComponents] = useState(() => (
    componentScoreSeries.reduce((acc, series) => ({
      ...acc,
      [series.key]: series.defaultVisible
    }), {})
  ));
  const resolvedSecurityId = security?.security_id || securityId || cusip;

  useEffect(() => {
    const fetchSecurityData = async () => {
      if (!resolvedSecurityId) {
        setLoading(false);
        return;
      }
      
      try {
        const token = localStorage.getItem('access_token');
        const response = await axios.get(
          `${process.env.REACT_APP_API_URL}/security/${resolvedSecurityId}/signals`,
          {
            headers: { Authorization: `Bearer ${token}` }
          }
        );
        setSecurityData(response.data);
      } catch (error) {
        console.error('Error fetching security data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchSecurityData();
  }, [resolvedSecurityId]);

  const toggleTooltip = (factor) => {
    if (activeTooltip === factor) {
      setActiveTooltip(null);
    } else {
      setActiveTooltip(factor);
    }
  };

  const toggleComponentSeries = (key) => {
    setVisibleComponents((prev) => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  const allComponentsVisible = componentScoreSeries.every((series) => visibleComponents[series.key]);

  const toggleAllComponents = () => {
    const nextVisible = !allComponentsVisible;
    setVisibleComponents(componentScoreSeries.reduce((acc, series) => ({
      ...acc,
      [series.key]: nextVisible,
    }), {}));
  };

  // Helper function to format score values
  const formatScore = (value) => {
    if (value === null || value === undefined || value === '') {
      return '–';
    }
    return Math.round(value);
  };

  // Helper function to get progress bar width
  const getProgressWidth = (value) => {
    if (value === null || value === undefined || value === '') {
      return 0;
    }
    return Math.round(value);
  };

  if (loading) {
    return <div className="min-h-screen bg-bg-100 p-4">
      <div className="animate-pulse flex space-x-4">
        <div className="flex-1 space-y-6 py-1">
          <div className="h-4 bg-bg-300 rounded"></div>
          <div className="space-y-3">
            <div className="h-4 bg-bg-300 rounded"></div>
          </div>
        </div>
      </div>
    </div>;
  }

  const securityInfo = securityData?.security_info || security || {};
  const isInactive = securityInfo.is_active === false;
  const signalHistory = securityData?.time_series || [];
  // Get latest monthly signal
  const latestSignal = signalHistory[0]?.signal !== undefined && signalHistory[0]?.signal !== null
    ? Math.round(signalHistory[0].signal)
    : null;

  return (
    <div className="min-h-screen bg-bg-100">
      <div className="mt-5 px-4 sm:px-6 lg:px-8">
        <button
          onClick={() => {
            if (returnTo) {
              navigate(returnTo.path, { state: { activeTab: returnTo.tab } });
            } else {
              navigate(-1);
            }
          }}
          className="mb-4 flex items-center text-sm text-text-200 hover:text-text-100"
        >
          <svg 
            className="mr-2 h-4 w-4" 
            fill="none" 
            stroke="currentColor" 
            viewBox="0 0 24 24"
          >
            <path 
              strokeLinecap="round" 
              strokeLinejoin="round" 
              strokeWidth={2} 
              d="M10 19l-7-7m0 0l7-7m-7 7h18" 
            />
          </svg>
          Back
        </button>

        <div className="sm:flex sm:items-center">
          <div className="sm:flex-auto">
            <div className="flex items-center gap-3">
              <h1 className="text-2xl font-semibold leading-6 text-text-100">{securityInfo.name || `Security ${resolvedSecurityId}`}</h1>
              {isInactive && (
                <span className="inline-flex items-center rounded-md bg-yellow-400/10 px-2 py-1 text-xs font-medium text-yellow-300">
                  Inactive
                </span>
              )}
            </div>
            <p className="mt-2 text-sm text-text-200">{securityInfo.ticker || resolvedSecurityId}</p>
            {securityData?.security_info?.sector && (
              <p className="mt-1 text-sm text-text-200">
                {securityData.security_info.sector} | {securityData.security_info.industry}
              </p>
            )}
            {isInactive && (
              <p className="mt-2 max-w-3xl text-sm text-yellow-300">
                {securityInfo.inactive_note || 'Inactive security - likely corporate action, delisting, or cash acquisition'}. Live market widgets are hidden; historical SharpeMetrix signal history is shown below when available.
              </p>
            )}
          </div>
        </div>

        {/* Signal Score Summary */}
        <div className="mt-6">
          <div className="flex items-center mb-5">
            <p className="bg-primary-300 text-white text-sm font-semibold inline-flex items-center p-1.5 rounded-sm">
              {latestSignal ?? '–'}
            </p>
            <p className="ms-2 font-medium text-text-100">
              Monthly Signal
            </p>
            <span className="w-1 h-1 mx-2 bg-bg-300 rounded-full"></span>
            <p className="text-sm font-medium text-text-200">
              {securityData?.as_of_date
                ? `As of ${formatLocalDate(securityData.as_of_date, { month: 'numeric', day: 'numeric', year: 'numeric' })}`
                : 'No signal history available'}
            </p>
          </div>

          {/* Signal Calculation Description */}
          <div className="mt-4 relative">
            <div className="bg-gradient-to-r from-blue-600/20 via-blue-500/15 to-cyan-500/20 rounded-lg border border-blue-400/30 p-4 backdrop-blur-sm">
              <div className="flex items-start space-x-3">
                <div className="flex-shrink-0">
                  <InformationCircleIcon className="h-5 w-5 text-blue-300" />
                </div>
                <p className="text-sm text-white leading-relaxed">
                  Signal scores are dynamically calculated from available fundamental, technical, and alternative data components at each processing date, 
                  optimized for institutional portfolio construction and risk management.
                </p>
              </div>
            </div>
          </div>

          {/* Component Scores */}
          {!isInactive && (
          <div className="mt-6 bg-bg-200/50 rounded-lg p-6 mb-8">
            <h2 className="text-xl font-semibold text-text-100 mb-6">Component Scores</h2>
            <div className="gap-8 sm:grid sm:grid-cols-2">
              <div className="space-y-6">
                {['value', 'momentum', 'quality', 'lowvol'].map((factor) => (
                  <dl key={factor} className="relative">
                    <dt className="text-sm font-medium text-text-200 capitalize mb-2 flex items-center">
                      {factor}
                      <button
                        type="button"
                        className="ml-1.5 text-text-300 hover:text-text-100 focus:outline-none"
                        onClick={() => toggleTooltip(factor)}
                        aria-label={`Information about ${factor}`}
                      >
                        <InformationCircleIcon className="h-4 w-4" aria-hidden="true" />
                      </button>
                      {activeTooltip === factor && (
                        <div className="absolute z-10 bg-bg-300 p-2 rounded-md shadow-lg text-xs text-text-100 max-w-xs left-0 top-6">
                          {signalDescriptions[factor]}
                        </div>
                      )}
                    </dt>
                    <dd className="flex items-center">
                      <div className="w-full bg-bg-300 rounded h-2.5 me-2">
                        <div 
                          className="bg-primary-100 h-2.5 rounded" 
                          style={{ width: `${getProgressWidth(securityData?.component_scores?.[`${factor}_score`])}%` }}
                        ></div>
                      </div>
                      <span className="text-sm font-medium text-text-200 min-w-[3rem] text-right">
                        {formatScore(securityData?.component_scores?.[`${factor}_score`])}
                      </span>
                    </dd>
                  </dl>
                ))}
              </div>
              <div className="space-y-6 mt-6 sm:mt-0">
                {['earnings', 'transcripts', 'innovation'].map((factor) => (
                  <dl key={factor} className="relative">
                    <dt className="text-sm font-medium text-text-200 capitalize mb-2 flex items-center">
                      {factor}
                      <button
                        type="button"
                        className="ml-1.5 text-text-300 hover:text-text-100 focus:outline-none"
                        onClick={() => toggleTooltip(factor)}
                        aria-label={`Information about ${factor}`}
                      >
                        <InformationCircleIcon className="h-4 w-4" aria-hidden="true" />
                      </button>
                      {activeTooltip === factor && (
                        <div className="absolute z-10 bg-bg-300 p-2 rounded-md shadow-lg text-xs text-text-100 max-w-xs left-0 top-6">
                          {signalDescriptions[factor]}
                        </div>
                      )}
                    </dt>
                    <dd className="flex items-center">
                      <div className="w-full bg-bg-300 rounded h-2.5 me-2">
                        <div 
                          className="bg-primary-100 h-2.5 rounded" 
                          style={{ width: `${getProgressWidth(securityData?.component_scores?.[`${factor}_score`])}%` }}
                        ></div>
                      </div>
                      <span className="text-sm font-medium text-text-200 min-w-[3rem] text-right">
                        {formatScore(securityData?.component_scores?.[`${factor}_score`])}
                      </span>
                    </dd>
                  </dl>
                ))}
              </div>
            </div>
          </div>
          )}

          {!isInactive && (
            <>
              {/* Full-width TradingView Symbol Profile Widget */}
              <div className="mt-6 bg-bg-200/50 rounded-lg p-4 h-[350px]">
                <TradingViewSymbolProfileWidget key={`profile-${securityInfo.ticker}`} ticker={securityInfo.ticker} />
              </div>

              {/* TradingView Chart and Company Description */}
              <div className="mt-6 grid grid-cols-5 gap-4">
                <div className="col-span-3 bg-bg-200/50 rounded-lg p-4 h-[400px]">
                  <TradingViewWidget key={`chart-${securityInfo.ticker}`} ticker={securityInfo.ticker} />
                </div>
                <div className="col-span-2 bg-bg-200/50 rounded-lg p-4 h-[400px]">
                  <CompanyDescriptionWidget key={`description-${securityInfo.ticker}`} ticker={securityInfo.ticker} />
                </div>
              </div>
            </>
          )}

          {/* Signal History Chart */}
          <div className="mt-6 bg-bg-200/50 rounded-lg p-4">
            <h2 className="text-xl font-semibold text-text-100 mb-4">Signal History</h2>
            <ResponsiveContainer width="100%" height={400}>
              <BarChart 
                data={[...signalHistory].reverse()}
                margin={{ top: 20, right: 30, left: 50, bottom: 20 }}
              >
                <XAxis 
                  dataKey="date"
                  tickFormatter={formatMonthYear}
                  tick={{ fill: 'white' }}
                  height={40}
                  padding={{ left: 16, right: 16 }}
                  tickMargin={8}
                />
                <YAxis
                  domain={[0, 100]}
                  tickCount={5}
                  tick={{ fill: 'white' }}
                  tickFormatter={(tick) => `${tick}`}
                  label={{ 
                    value: 'Signal Score', 
                    angle: -90, 
                    position: 'insideLeft',
                    offset: -5,
                    style: { fill: 'white', textAnchor: 'middle' } 
                  }}
                />
                <Tooltip
                  contentStyle={{ backgroundColor: '#292a37', border: 'none' }}
                  labelStyle={{ color: '#fff' }}
                  itemStyle={{ color: '#fff' }}
                  formatter={(value) => [`${Math.round(value)}`, 'Signal']}
                  labelFormatter={formatMonthYear}
                />
                <Bar dataKey="signal" fill="#2E5BFF" />
              </BarChart>
            </ResponsiveContainer>
          </div>

          {/* Historical Component Scores Chart */}
          <div className="mt-6 bg-bg-200/50 rounded-lg p-4">
            <div className="flex flex-col gap-3 mb-4 sm:flex-row sm:items-start sm:justify-between">
              <div>
                <h2 className="text-xl font-semibold text-text-100">Historical Component Scores</h2>
                <p className="mt-1 text-sm text-text-200">
                  Track how each input score changed over time. Toggle series to reduce chart noise.
                </p>
              </div>
              <div className="flex flex-wrap justify-end gap-2">
                <button
                  type="button"
                  onClick={toggleAllComponents}
                  className="rounded-full border border-primary-100 bg-bg-100 px-3 py-1 text-xs font-semibold text-primary-100 hover:bg-primary-100/10"
                >
                  {allComponentsVisible ? 'Hide All' : 'Select All'}
                </button>
                {componentScoreSeries.map((series) => (
                  <button
                    key={series.key}
                    type="button"
                    onClick={() => toggleComponentSeries(series.key)}
                    className={`rounded-full border px-3 py-1 text-xs font-medium ${
                      visibleComponents[series.key]
                        ? 'border-primary-100 bg-primary-100/20 text-text-100'
                        : 'border-bg-300 bg-bg-300/30 text-text-200'
                    }`}
                  >
                    {series.label}
                  </button>
                ))}
              </div>
            </div>
            <ResponsiveContainer width="100%" height={400}>
              <LineChart
                data={[...signalHistory].reverse()}
                margin={{ top: 20, right: 30, left: 50, bottom: 20 }}
              >
                <CartesianGrid stroke="#2a2b3a" />
                <XAxis
                  dataKey="date"
                  tickFormatter={formatMonthYear}
                  tick={{ fill: 'white' }}
                  height={40}
                  padding={{ left: 16, right: 16 }}
                  tickMargin={8}
                />
                <YAxis
                  domain={[0, 100]}
                  tickCount={5}
                  tick={{ fill: 'white' }}
                  tickFormatter={(tick) => `${tick}`}
                  label={{
                    value: 'Component Score',
                    angle: -90,
                    position: 'insideLeft',
                    offset: -5,
                    style: { fill: 'white', textAnchor: 'middle' }
                  }}
                />
                <Tooltip
                  contentStyle={{ backgroundColor: '#292a37', border: 'none' }}
                  labelStyle={{ color: '#fff' }}
                  itemStyle={{ color: '#fff' }}
                  formatter={(value, name) => [
                    value === null || value === undefined ? '–' : `${Math.round(value)}`,
                    name
                  ]}
                  labelFormatter={formatMonthYear}
                />
                <Legend wrapperStyle={{ color: 'white' }} />
                {componentScoreSeries
                  .filter((series) => visibleComponents[series.key])
                  .map((series) => (
                    <Line
                      key={series.key}
                      type="monotone"
                      dataKey={series.key}
                      stroke={series.color}
                      name={series.label}
                      dot={false}
                      strokeWidth={2}
                      connectNulls
                      isAnimationActive={false}
                    />
                  ))}
              </LineChart>
            </ResponsiveContainer>
          </div>

          {/* Signal History Table */}
          <div className="mt-8">
            <h3 className="text-xl font-semibold leading-6 text-text-100 mb-4">Historical Signals</h3>
            <div className="overflow-hidden shadow ring-1 ring-black ring-opacity-5 sm:rounded-lg bg-bg-200">
              <div className="m-2">
                <div className="max-h-[400px] overflow-y-auto">
                  <table className="min-w-full divide-y divide-bg-300">
                    <thead className="bg-bg-200 sticky top-0 z-10">
                      <tr>
                        <th scope="col" className="whitespace-nowrap px-2 py-3.5 text-left text-sm font-semibold text-text-100">Date</th>
                        <th scope="col" className="whitespace-nowrap px-2 py-3.5 text-right text-sm font-semibold text-text-100">Signal Score</th>
                        {componentScoreSeries.map((series) => (
                          <th key={series.key} scope="col" className="whitespace-nowrap px-2 py-3.5 text-right text-sm font-semibold text-text-100">
                            {series.label}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-bg-300 bg-bg-200">
                      {signalHistory.map((point) => (
                        <tr key={point.date}>
                          <td className="whitespace-nowrap px-2 py-2 text-sm text-text-200 text-left">
                            {formatLocalDate(point.date, { month: 'numeric', day: 'numeric', year: 'numeric' })}
                          </td>
                          <td className="whitespace-nowrap px-2 py-2 text-sm text-text-200 text-right">
                            {Math.round(point.signal)}
                          </td>
                          {componentScoreSeries.map((series) => (
                            <td key={series.key} className="whitespace-nowrap px-2 py-2 text-sm text-text-200 text-right">
                              {formatScore(point[series.key])}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
          <br />
        </div>
      </div>
      <br />
      <DisclaimerFooter small />
    </div>
  );
} 